import { Injectable, inject, signal } from '@angular/core';
import { QueryBuilder, QueryFilter } from '@softline/core';
import { ClaimReportCustomerRepository } from '../repositories/claim-report-customer.repository';
import {
  ClaimReportCustomerAggregatedRow,
  ClaimReportCustomerQueryRow,
  ClaimReportCustomerSummary,
} from '../types/claim-report-document-query';

const PAGE_SIZE = 50;

@Injectable()
export class ClaimReportCustomerService {
  private readonly repository = inject(ClaimReportCustomerRepository);

  readonly customers = signal<ClaimReportCustomerSummary[]>([]);
  readonly selected = signal<ClaimReportCustomerSummary | null>(null);
  readonly isLoading = signal(false);
  readonly isLoadingMore = signal(false);
  readonly hasMore = signal(false);
  readonly error = signal<unknown>(null);

  private generation = 0;
  private offset = 0;
  private term: string | undefined;

  async load(search?: string | null): Promise<void> {
    const generation = ++this.generation;
    this.term = search?.trim() || undefined;
    this.offset = 0;
    this.hasMore.set(false);
    this.isLoadingMore.set(false);
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const page = await this.fetchPage({ term: this.term, start: 0 });
      if (generation !== this.generation) return;
      this.customers.set(page);
      this.offset = page.length;
      this.hasMore.set(page.length === PAGE_SIZE);
    } catch (e) {
      if (generation === this.generation) this.error.set(e);
      throw e;
    } finally {
      if (generation === this.generation) this.isLoading.set(false);
    }
  }

  async loadMore(): Promise<void> {
    if (this.isLoading() || this.isLoadingMore() || !this.hasMore()) return;
    const generation = this.generation;
    this.isLoadingMore.set(true);
    try {
      const page = await this.fetchPage({ term: this.term, start: this.offset });
      if (generation !== this.generation) return;
      this.customers.update((current) => [...current, ...page]);
      this.offset += page.length;
      this.hasMore.set(page.length === PAGE_SIZE);
    } finally {
      if (generation === this.generation) this.isLoadingMore.set(false);
    }
  }

  async loadCustomer(customerNumber: number): Promise<void> {
    const [customer] = await this.fetchPage({ number: customerNumber, start: 0 });
    this.selected.set(customer ?? null);
  }

  private async fetchPage(options: {
    term?: string;
    number?: number;
    start: number;
  }): Promise<ClaimReportCustomerSummary[]> {
    const builder = new QueryBuilder<ClaimReportCustomerQueryRow>()
      .column('invoice_customer.number')
      .column('invoice_customer.name')
      .column('sales_order.id', { agg: 'count' })
      .sort('invoice_customer.number', 'DESC')
      //it-konzept: Eine Shop-Bestellung erzeugt einen nicht freigegebenen Auftrag mit dem Kennzeichen „kzvkbranche“ = „V“ (Mietvertrag)
      .filter({
        type: 'stringEquals',
        name: 'sales_order.sale_department',
        value: 'V',
      });
    if (options.number !== undefined)
      builder.filter({
        type: 'numberEquals',
        name: 'invoice_customer.number',
        value: options.number,
      });
    if (options.term) {
      const matches: QueryFilter<ClaimReportCustomerQueryRow>[] = [
        {
          type: 'stringContainsIgnoreCase',
          name: 'invoice_customer.name',
          value: options.term,
        },
      ];
      if (/^\d+$/.test(options.term))
        matches.push(
          {
            type: 'numberEquals',
            name: 'invoice_customer.number',
            value: Number(options.term),
          },
          {
            type: 'numberEquals',
            name: 'sales_order.document_number',
            value: Number(options.term),
          }
        );
      builder.filter({ type: 'conditions', operator: 'OR', filters: matches });
    }
    const size = options.number !== undefined ? 1 : PAGE_SIZE;
    const query = builder
      .start(options.start)
      .end(options.start + size)
      .build();
    const result = await this.repository.load({ query });
    const rows = (result?.data ?? []) as unknown as ClaimReportCustomerAggregatedRow[];
    return rows.map((row) => ({
      number: row.invoice_customer.number,
      name: row.invoice_customer.name,
      documentCount: row.sales_order['id:count'],
    }));
  }
}
